import NavBar from '../components/NavBar'
import { Link } from 'react-router'
import { ArrowRight, Check } from "lucide-react"
import Button from '../components/ui/Button'
import { useRef } from 'react';

function Home() {
  const featureSection = useRef(null);
  
  return (
    <div className="min-h-screen relative overflow-hidden bg-white">
      <div className="fixed top-0 left-0 right-0 z-50">
        <NavBar />
      </div>

      <div className="h-20"></div>

      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-3/4 h-3/4 bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 rounded-full opacity-80 blur-3xl"></div>
        <div className="absolute -bottom-32 -right-32 w-full h-full bg-gradient-to-tl from-blue-500/20 via-blue-200/20 to-blue-50/20 rounded-full opacity-80 blur-3xl"></div>

        {/* Animated dots */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse absolute w-1 h-1 bg-blue-500/30 rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 3}s`,
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Hero content */}
      <section className="relative z-10 container mx-auto px-4">
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] text-center">
          <div className="space-y-6 max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900">
              Master Your Exams with{" "}
              <span className="bg-gradient-to-r from-blue-600 to-primary bg-clip-text text-transparent">
                Confidence
              </span>
            </h1>
            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
              Interactive quizzes, comprehensive study materials, and personalized learning paths to help you achieve
              academic excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to={'/discover'} size="lg" className="btn btn-primary px-8">
                Start Learning
              </Link>
              <Link
                to={'/discover'}
                size="lg"
                className="btn btn-primary btn-outline px-8"
              >
                View Courses
              </Link>
            </div>
          </div>

          {/* Floating stats cards */}
          <div className="absolute top-1/4 left-10 animate-float-slow">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-blue-100">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-blue-600">500+</span>
                <span className="text-sm text-gray-600">Practice Tests</span>
              </div>
            </div>
          </div>
          <div className="absolute bottom-1/4 right-10 animate-float">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-blue-100">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-blue-600">50K+</span>
                <span className="text-sm text-gray-600">Students</span>
              </div>
            </div>
          </div>

          {/* Scroll down indicator */}
          <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
            <button
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/80 shadow-md hover:bg-white transition-colors"
              onClick={(e) => {
                e.preventDefault()
                featureSection.current?.scrollIntoView({ behavior: 'smooth' });
              }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

        </div>
      </section>

      {/* Features section with side-by-side layout */}
      <section ref={featureSection} className="py-24 relative z-10">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Designed for Both <span className="text-blue-600">Educators</span> and{" "}
              <span className="text-blue-600">Students</span>
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Our platform connects educators and students in a seamless learning experience
            </p>
          </div>

          {/* Educator feature - side by side */}
          <div className="flex flex-col md:flex-row items-center gap-12 mb-24">
            <div className="w-full md:w-1/2 order-2 md:order-1">
              <h3 className="text-2xl font-bold text-gray-900">For Educators</h3>
              <h4 className="text-xl font-semibold text-gray-900 mt-4">Effortless Question Creation</h4>
              <p className="mt-4 text-gray-600">
                Our platform makes it simple to create, organize, and manage your question bank. Add multiple choice,
                short answer, or essay questions with rich formatting options.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Create rich multimedia questions</span>
                </li>
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Organize questions by topic and difficulty</span>
                </li>
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Track student performance with detailed analytics</span>
                </li>
              </ul>
              <div className="mt-8">
                <Link
                  to="/for-educators"
                  className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                >
                  Learn more about educator tools
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="w-full md:w-1/2 order-1 md:order-2 ">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 group-hover:shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img
                  src="/teacher-creating-quiz.png"
                  alt="Lecturer creating quiz questions"
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>

          {/* Student feature - side by side */}
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 group-hover:shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <img
                  src="/student-taking-quiz.png"
                  alt="Student taking an online quiz"
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <h3 className="text-2xl font-bold text-gray-900">For Students</h3>
              <h4 className="text-xl font-semibold text-gray-900 mt-4">Interactive Learning Experience</h4>
              <p className="mt-4 text-gray-600">
                Engage with interactive quizzes that adapt to your learning style. Get instant feedback and detailed
                explanations to improve your understanding.
              </p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Take quizzes on any device, anytime</span>
                </li>
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Receive instant feedback and explanations</span>
                </li>
                <li className="flex items-start">
                  <Check className='size-6 text-blue-500 mr-2'/>
                  <span>Track your progress with detailed performance insights</span>
                </li>
              </ul>
              <div className="mt-8">
                <Link
                  to="/for-students"
                  className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                >
                  Discover student features
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Call to action */}
          <div className="mt-20 text-center">
            <div className="inline-block bg-blue-50 px-6 py-3 rounded-full">
              <p className="text-blue-700 font-medium">Ready to transform your learning experience?</p>
            </div>
            <div className="mt-8">
              <Link
                to="/signup"
              >
                <Button className='px-8'>
                  Get Started Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}

export default Home